import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { Project, ReadyMonthStatRow } from '@/api/client';
import {
  areReadyMonthBucketsEqual,
  bucketToReadyMonthYearMonth,
  getReadyByMonthBucketFromProject,
  parseYearMonthKey,
} from '@/utils/readyMonthStats';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';
import { sortProjects } from '@/utils/sortProjects';
import {
  PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY,
  ARCHIVED_PROJECT_IDS_QUERY_KEY,
  ARCHIVED_COUNT_QUERY_KEY,
  getProjectEntityFromCaches,
  readyMonthProjectIdsQueryKey,
  projectEntityQueryKey,
  setProjectEntity,
  syncProjectEntities,
  syncProjectIdsQuery,
} from '@/lib/projectEntityCache';
import { projectScheduleSplitFieldsEqual } from '@/schedule/scheduleFinalStatus';

/** Кэш проектов для split/«Далее:» на сетке расписания (SchedulePage). */
export const PROJECTS_FOR_SCHEDULE_FINAL_QUERY_KEY = ['projects', 'for-schedule-final'] as const;

type ScheduleFinalProjectsCache =
  | Project[]
  | { items: Project[]; hasMore?: boolean };

function readScheduleFinalProjectsList(
  data: ScheduleFinalProjectsCache | undefined
): Project[] | undefined {
  if (!data) return undefined;
  return Array.isArray(data) ? data : data.items;
}

function writeScheduleFinalProjectsList(
  prev: ScheduleFinalProjectsCache | undefined,
  nextList: Project[]
): ScheduleFinalProjectsCache {
  if (prev && !Array.isArray(prev)) {
    return { ...prev, items: nextList };
  }
  return nextList;
}

function mergeScheduleFinalSplitFields(existing: Project | undefined, incoming: Project): Project {
  const base = existing ?? incoming;
  return {
    ...base,
    name: incoming.name,
    state: incoming.state,
    deadline: incoming.deadline ?? existing?.deadline ?? null,
    readers: incoming.readers ?? existing?.readers ?? [],
    sessions: existing?.sessions ?? [],
  };
}

/**
 * Точечно обновляет проект в кэше split-статусов расписания.
 * Пишет в кэш только если изменились поля, от которых зависят split/«Далее:».
 * Иначе сетка получает новый массив проектов и перерисовывает все сессии.
 */
export function patchScheduleFinalProjectsCache(
  queryClient: QueryClient,
  project: Project
): void {
  const prev = queryClient.getQueryData<ScheduleFinalProjectsCache>(
    PROJECTS_FOR_SCHEDULE_FINAL_QUERY_KEY
  );
  if (prev === undefined) return;
  const list = readScheduleFinalProjectsList(prev) ?? [];
  const idx = list.findIndex((p) => p.id === project.id);
  if (idx >= 0 && projectScheduleSplitFieldsEqual(list[idx], project)) return;
  const merged = mergeScheduleFinalSplitFields(idx >= 0 ? list[idx] : undefined, project);
  const nextList =
    idx >= 0 ? list.map((p, i) => (i === idx ? merged : p)) : [merged, ...list];
  const reused = reuseProjectListIfUnchanged(list, nextList);
  if (reused === list) return;
  queryClient.setQueryData(
    PROJECTS_FOR_SCHEDULE_FINAL_QUERY_KEY,
    writeScheduleFinalProjectsList(prev, nextList)
  );
}

export function removeProjectFromScheduleFinalCache(
  queryClient: QueryClient,
  projectId: string
): void {
  const prev = queryClient.getQueryData<ScheduleFinalProjectsCache>(
    PROJECTS_FOR_SCHEDULE_FINAL_QUERY_KEY
  );
  if (prev === undefined) return;
  const list = readScheduleFinalProjectsList(prev) ?? [];
  const nextList = list.filter((p) => p.id !== projectId);
  if (nextList.length === list.length) return;
  queryClient.setQueryData(
    PROJECTS_FOR_SCHEDULE_FINAL_QUERY_KEY,
    writeScheduleFinalProjectsList(prev, nextList)
  );
}

export function readyMonthProjectIdsPredicate(q: { queryKey: unknown }): boolean {
  const k = q.queryKey;
  return Array.isArray(k) && k[0] === 'ready-month-project-ids';
}

export function archivedProjectIdsPredicate(q: { queryKey: unknown }): boolean {
  const k = q.queryKey;
  return Array.isArray(k) && k[0] === ARCHIVED_PROJECT_IDS_QUERY_KEY[0];
}

export type ProjectListRollbackContext = {
  activeIds?: string[];
  archivedIds?: string[];
  readyMonthIds: Array<[QueryKey, string[] | undefined]>;
  projectEntities: Array<[string, Project | undefined]>;
  readyMonthSummaries?: ReadyMonthStatRow[];
};

export function captureProjectListRollback(queryClient: QueryClient): ProjectListRollbackContext {
  const activeIds = queryClient.getQueryData<string[]>(PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY);
  const archivedIds = queryClient.getQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY);
  const readyMonthIds = queryClient.getQueriesData<string[]>({ predicate: readyMonthProjectIdsPredicate });
  const projectEntityIds = [
    ...new Set([
      ...(activeIds ?? []),
      ...(archivedIds ?? []),
      ...readyMonthIds.flatMap(([, ids]) => ids ?? []),
    ]),
  ];
  return {
    activeIds,
    archivedIds,
    readyMonthIds,
    projectEntities: projectEntityIds.map((id) => [
      id,
      queryClient.getQueryData<Project>(projectEntityQueryKey(id)),
    ]),
    readyMonthSummaries: queryClient.getQueryData<ReadyMonthStatRow[]>(['ready-month-summaries']),
  };
}

export function restoreProjectListRollback(
  queryClient: QueryClient,
  ctx: ProjectListRollbackContext
): void {
  if (ctx.activeIds !== undefined) {
    queryClient.setQueryData(PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY, ctx.activeIds);
  }
  if (ctx.archivedIds !== undefined) {
    queryClient.setQueryData(ARCHIVED_PROJECT_IDS_QUERY_KEY, ctx.archivedIds);
  }
  for (const [key, data] of ctx.readyMonthIds) {
    queryClient.setQueryData(key, data);
  }
  for (const [projectId, entity] of ctx.projectEntities) {
    queryClient.setQueryData(projectEntityQueryKey(projectId), entity);
  }
  if (ctx.readyMonthSummaries !== undefined) {
    queryClient.setQueryData(['ready-month-summaries'], ctx.readyMonthSummaries);
  }
  syncProjectCollectionsSecondaryCaches(queryClient);
}

/**
 * Если updater вернул новый массив с теми же ссылками на элементы — оставляем prev.
 * Иначе патч активной строки создаёт новый массив в каждом кэше ready-month и React Query
 * помечает все месяцы изменёнными, хотя проект в списке мог не находиться.
 */
/** Экспорт для reconcile: сохранять ссылку на массив, если состав и ссылки на элементы не изменились. */
export function reuseProjectListIfUnchanged(
  prev: Project[] | undefined,
  next: Project[] | undefined
): Project[] | undefined {
  if (next === prev) return prev;
  if (!next || !prev) return next;
  if (next.length !== prev.length) return next;
  for (let i = 0; i < prev.length; i++) {
    if (prev[i] !== next[i]) return next;
  }
  return prev;
}

export function syncProjectCollectionsSecondaryCaches(queryClient: QueryClient): void {
  const activeProjects = getActiveProjectsSnapshot(queryClient);
  if (!activeProjects) return;
  syncProjectEntities(queryClient, activeProjects);
}

export function setActiveProjectsSnapshot(
  queryClient: QueryClient,
  projects: Project[]
): void {
  const sorted = sortProjects(projects);
  syncProjectEntities(queryClient, sorted);
  syncProjectIdsQuery(queryClient, PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY, sorted);
  syncProjectCollectionsSecondaryCaches(queryClient);
}

export function getActiveProjectsSnapshot(queryClient: QueryClient): Project[] | undefined {
  const ids = queryClient.getQueryData<string[]>(PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY);
  if (ids === undefined) return undefined;
  return ids
    .map((projectId) => getProjectEntityFromCaches(queryClient, projectId))
    .filter((project): project is Project => !!project);
}

export function setReadyMonthProjectsSnapshot(
  queryClient: QueryClient,
  bucket: { year: number; month: number },
  projects: Project[]
): void {
  const sorted = sortProjects(projects);
  syncProjectEntities(queryClient, sorted);
  syncProjectIdsQuery(queryClient, readyMonthProjectIdsQueryKey(bucket.year, bucket.month), sorted);
  ensureReadyMonthSummaryRow(queryClient, bucket);
}

export function getReadyMonthProjectsSnapshot(
  queryClient: QueryClient,
  bucket: { year: number; month: number }
): Project[] | undefined {
  const ids = queryClient.getQueryData<string[]>(readyMonthProjectIdsQueryKey(bucket.year, bucket.month));
  if (ids === undefined) return undefined;

  return ids
    .map((projectId) => getProjectEntityFromCaches(queryClient, projectId))
    .filter((project): project is Project => !!project);
}

export function setArchivedProjectsSnapshot(queryClient: QueryClient, projects: Project[]): void {
  syncProjectEntities(queryClient, projects);
  syncProjectIdsQuery(queryClient, ARCHIVED_PROJECT_IDS_QUERY_KEY, projects);
  syncProjectCollectionsSecondaryCaches(queryClient);
}

export function getArchivedProjectsSnapshot(queryClient: QueryClient): Project[] | undefined {
  const ids = queryClient.getQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY);
  if (ids === undefined) return undefined;
  return ids
    .map((projectId) => getProjectEntityFromCaches(queryClient, projectId))
    .filter((project): project is Project => !!project);
}

function ensureReadyMonthSummaryRow(
  queryClient: QueryClient,
  bucket: { year: number; month: number }
): void {
  const yearMonth = bucketToReadyMonthYearMonth(bucket);
  queryClient.setQueryData<ReadyMonthStatRow[]>(['ready-month-summaries'], (prev) => {
    const list = prev ?? [];
    if (list.some((row) => row.year_month === yearMonth)) return list;
    return [...list, { year_month: yearMonth }].sort((a, b) => a.year_month.localeCompare(b.year_month));
  });
}

function pruneReadyMonthSummaryRowIfEmpty(
  queryClient: QueryClient,
  bucket: { year: number; month: number }
): void {
  const cache = queryClient.getQueryData<string[]>(readyMonthProjectIdsQueryKey(bucket.year, bucket.month));
  if (cache === undefined || cache.length > 0) return;

  const yearMonth = bucketToReadyMonthYearMonth(bucket);
  queryClient.setQueryData<ReadyMonthStatRow[]>(['ready-month-summaries'], (prev) => {
    if (!prev) return prev;
    const next = prev.filter((row) => row.year_month !== yearMonth);
    return next.length === prev.length ? prev : next;
  });
}

/**
 * Только активная таблица (без кэшей «готовых по месяцам»).
 * Для создания/операций, где нельзя дублировать запись в раскрытых месяцах готовых.
 */
export function patchActiveTableListCaches(
  queryClient: QueryClient,
  updater: (prev: Project[] | undefined) => Project[] | undefined
): void {
  const prev = getActiveProjectsSnapshot(queryClient);
  const next = reuseProjectListIfUnchanged(prev, updater(prev));
  if (next !== undefined) {
    setActiveProjectsSnapshot(queryClient, next);
  }
}

/** Патч active ids/entity-first слоя и, при наличии, готовых по месяцам. */
export function patchProjectListCaches(
  queryClient: QueryClient,
  updater: (prev: Project[] | undefined) => Project[] | undefined
): void {
  const wrap = (prev: Project[] | undefined) => reuseProjectListIfUnchanged(prev, updater(prev));
  const activePrev = getActiveProjectsSnapshot(queryClient);
  const activeNext = wrap(activePrev);
  if (activeNext !== undefined) {
    setActiveProjectsSnapshot(queryClient, activeNext);
  }
  const readyEntries = queryClient.getQueriesData<string[]>({ predicate: readyMonthProjectIdsPredicate });
  for (const [queryKey] of readyEntries) {
    const key = queryKey as unknown[];
    const year = key[1];
    const month = key[2];
    if (typeof year !== 'number' || typeof month !== 'number') continue;
    const prevProjects = getReadyMonthProjectsSnapshot(queryClient, { year, month });
    const nextProjects = wrap(prevProjects);
    if (nextProjects !== undefined) {
      setReadyMonthProjectsSnapshot(queryClient, { year, month }, nextProjects);
    }
  }
  const archivedPrev = getArchivedProjectsSnapshot(queryClient);
  const archivedNext = wrap(archivedPrev);
  if (archivedNext !== undefined) {
    setArchivedProjectsSnapshot(queryClient, archivedNext);
  }
}

function mergeProjectIntoList(
  prev: Project[] | undefined,
  project: Project
): Project[] | undefined {
  if (!prev) return prev;
  const excluded =
    isProjectExcludedFromActiveTable(project.state) || !!project.is_archived;
  const idx = prev.findIndex((p) => p.id === project.id);
  if (excluded) {
    if (idx < 0) return prev;
    return prev.filter((p) => p.id !== project.id);
  }
  const sessions = project.sessions ?? (idx >= 0 ? prev[idx].sessions : undefined) ?? [];
  const merged = { ...project, sessions };
  if (idx >= 0) {
    return prev.map((p) => (p.id === project.id ? merged : p));
  }
  return [merged, ...prev];
}

/**
 * Атомарно применяет ответ сервера во все кэши списков + одиночный кэш ['project', id].
 * Сохраняет sessions из кэша если сервер не вернул их.
 * Используй в onSuccess мутаций вместо invalidateQueries для проектов.
 *
 * Правило «исключить из активной таблицы» (Готов / архив) не применяется к кэшам готовых
 * по месяцам — там проект обновляется in-place в своём месяце.
 */
export function mergeSingleProjectInCaches(
  queryClient: QueryClient,
  project: Project
): void {
  // Черновик видит только автор: как и в realtime, он живёт лишь в своей сущности, не в списках и расписании
  if (project.is_draft === true) {
    setProjectEntity(queryClient, project);
    return;
  }
  patchActiveTableListCaches(queryClient, (prev) => mergeProjectIntoList(prev, project));
  patchScheduleFinalProjectsCache(queryClient, project);

  const archivedPrev = getArchivedProjectsSnapshot(queryClient);
  if (archivedPrev !== undefined) {
    const archivedNext = reuseProjectListIfUnchanged(
      archivedPrev,
      mergeProjectIntoList(archivedPrev, project)
    );
    if (archivedNext !== undefined) {
      setArchivedProjectsSnapshot(queryClient, archivedNext);
    }
  }

  const nowReady =
    isProjectExcludedFromActiveTable(project.state) && !project.is_archived;
  if (nowReady) {
    const bucket = getReadyByMonthBucketFromProject(project);
    removeProjectFromReadyMonthCachesOnly(queryClient, project.id);
    if (
      bucket &&
      queryClient.getQueryData<string[]>(readyMonthProjectIdsQueryKey(bucket.year, bucket.month)) !==
        undefined
    ) {
      mergeProjectIntoSingleReadyMonthCache(queryClient, bucket, project);
    }
  } else {
    removeProjectFromReadyMonthCachesOnly(queryClient, project.id);
  }

  queryClient.setQueryData<Project>(['project', project.id], (prev) =>
    prev ? { ...project, sessions: project.sessions ?? prev.sessions ?? [] } : project
  );
  setProjectEntity(queryClient, project);
  syncProjectCollectionsSecondaryCaches(queryClient);
}

/** Только active ids/entity-first слой и карточка — без списков готовых по месяцам. */
export function mergeSingleProjectInGlobalListCaches(
  queryClient: QueryClient,
  project: Project
): void {
  patchActiveTableListCaches(queryClient, (prev) => mergeProjectIntoList(prev, project));
  patchScheduleFinalProjectsCache(queryClient, project);
  queryClient.setQueryData<Project>(['project', project.id], (prev) =>
    prev ? { ...project, sessions: project.sessions ?? prev.sessions ?? [] } : project
  );
  setProjectEntity(queryClient, project);
  syncProjectCollectionsSecondaryCaches(queryClient);
}

/** Один ids/entity-first snapshot месяца: вставить/обновить строку и пересобрать отсортированный id[] списка. */
export function mergeProjectIntoSingleReadyMonthCache(
  queryClient: QueryClient,
  bucket: { year: number; month: number },
  project: Project
): void {
  const prev = getReadyMonthProjectsSnapshot(queryClient, bucket) ?? [];
  const existing = prev.find((p) => p.id === project.id);
  const without = prev.filter((p) => p.id !== project.id);
  const normalized = {
    ...project,
    sessions: project.sessions ?? existing?.sessions ?? [],
  };
  setReadyMonthProjectsSnapshot(queryClient, bucket, [...without, normalized]);
  setProjectEntity(queryClient, project);
  syncProjectCollectionsSecondaryCaches(queryClient);
}

export function mergeProjectIntoArchivedCache(queryClient: QueryClient, project: Project): void {
  const prev = getArchivedProjectsSnapshot(queryClient) ?? [];
  const existing = prev.find((p) => p.id === project.id);
  const without = prev.filter((p) => p.id !== project.id);
  const normalized = {
    ...project,
    sessions: project.sessions ?? existing?.sessions ?? [],
  };
  setArchivedProjectsSnapshot(queryClient, [...without, normalized]);
  setProjectEntity(queryClient, project);
  syncProjectCollectionsSecondaryCaches(queryClient);
}

export function removeProjectFromArchivedCachesOnly(queryClient: QueryClient, projectId: string): void {
  const prev = queryClient.getQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY);
  if (!prev) return;
  const next = prev.filter((id) => id !== projectId);
  queryClient.setQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY, (current) =>
    current ? (current.length === next.length && current.every((id, i) => id === next[i]) ? current : next) : current
  );
  syncProjectCollectionsSecondaryCaches(queryClient);
}

export type SyncAfterStatusEventsOptions = {
  /** Не инвалидировать сводку месяцев — нужен оптимистичный патч `optimisticPatchReadyMonthSummariesAfterBucketMove`. */
  skipReadyMonthSummariesInvalidation?: boolean;
};

/**
 * Оптимистично: добавить месяц в разделители, если его ещё не было; убрать месяц, если в кэше списка 0 проектов после переноса.
 */
export function optimisticPatchReadyMonthSummariesAfterBucketMove(
  queryClient: QueryClient,
  params: {
    prevB: { year: number; month: number } | null;
    nextB: { year: number; month: number } | null;
  }
): void {
  const { prevB, nextB } = params;
  if (areReadyMonthBucketsEqual(prevB, nextB)) return;

  queryClient.setQueryData<ReadyMonthStatRow[]>(['ready-month-summaries'], (prev) => {
    let list = [...(prev ?? [])];
    const monthKey = (ym: string) => parseYearMonthKey(ym).key;

    if (nextB) {
      const ym = bucketToReadyMonthYearMonth(nextB);
      const nk = monthKey(ym);
      if (!list.some((r) => monthKey(r.year_month) === nk)) {
        list.push({ year_month: ym });
      }
    }

    if (prevB) {
      const cached = queryClient.getQueryData<string[]>(
        readyMonthProjectIdsQueryKey(prevB.year, prevB.month)
      );
      if (cached !== undefined && cached.length === 0) {
        const pk = monthKey(bucketToReadyMonthYearMonth(prevB));
        list = list.filter((r) => monthKey(r.year_month) !== pk);
      }
    }

    list.sort((a, b) => a.year_month.localeCompare(b.year_month));
    return list;
  });
}

/**
 * После правок дат в журнале статусов для «Готов»: нельзя вызывать mergeSingleProjectInCaches —
 * он обновляет проект по id во всех кэшах месяцев, и строка остаётся в старом месяце.
 * Патчим только нужный ключ(и): при смене месяца — убрать id изо всех списков, затем вставить в nextB.
 */
export function syncProjectListCachesAfterStatusEventsChanged(
  queryClient: QueryClient,
  previous: Project,
  next: Project,
  options?: SyncAfterStatusEventsOptions
): void {
  if (!isProjectExcludedFromActiveTable(next.state) && !next.is_archived) {
    mergeSingleProjectInCaches(queryClient, next);
    return;
  }
  if (next.is_archived) {
    mergeSingleProjectInGlobalListCaches(queryClient, next);
    removeProjectFromReadyMonthCachesOnly(queryClient, next.id);
    if (queryClient.getQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY) !== undefined) {
      mergeProjectIntoArchivedCache(queryClient, next);
    }
    return;
  }
  const prevB = getReadyByMonthBucketFromProject(previous);
  const nextB = getReadyByMonthBucketFromProject(next);
  mergeSingleProjectInGlobalListCaches(queryClient, next);

  if (areReadyMonthBucketsEqual(prevB, nextB)) {
    if (nextB) mergeProjectIntoSingleReadyMonthCache(queryClient, nextB, next);
  } else {
    removeProjectFromReadyMonthCachesOnly(queryClient, next.id);
    if (nextB) mergeProjectIntoSingleReadyMonthCache(queryClient, nextB, next);
  }

  if (!options?.skipReadyMonthSummariesInvalidation) {
    optimisticPatchReadyMonthSummariesAfterBucketMove(queryClient, { prevB, nextB });
  }
}

function projectInAnyReadyMonthCache(queryClient: QueryClient, projectId: string): boolean {
  const entries = queryClient.getQueriesData<string[]>({ predicate: readyMonthProjectIdsPredicate });
  for (const [, data] of entries) {
    if (data?.includes(projectId)) return true;
  }
  return false;
}

/** Удаляет проект только из кэшей `ready-month-project-ids` (не трогает active ids/entity-first слой). */
export function removeProjectFromReadyMonthCachesOnly(
  queryClient: QueryClient,
  projectId: string
): void {
  const entries = queryClient.getQueriesData<string[]>({ predicate: readyMonthProjectIdsPredicate });
  for (const [queryKey, prev] of entries) {
    if (!prev) continue;
    const next = prev.filter((id) => id !== projectId);
    queryClient.setQueryData<string[]>(queryKey, (current) =>
      current ? current.length === next.length && current.every((id, index) => id === next[index]) ? current : next : current
    );

    const key = queryKey as unknown[];
    const year = key[1];
    const month = key[2];
    if (next.length === 0 && prev.length > 0 && typeof year === 'number' && typeof month === 'number') {
      pruneReadyMonthSummaryRowIfEmpty(queryClient, { year, month });
    }
  }
  syncProjectCollectionsSecondaryCaches(queryClient);
}

/** Точечный refetch списков готовых: один месяц или весь набор. */
export type ReadyMonthListInvalidation =
  | 'none'
  | 'all'
  | { year: number; month: number };

const READY_MONTH_INVALIDATE = {
  queryKey: ['ready-month-project-ids'] as const,
  /** Иначе по умолчанию refetch только у активных observer — месяц может быть в кэше, но `enabled: false`. */
  refetchType: 'all' as const,
};

export function invalidateReadyMonthProjectLists(
  queryClient: QueryClient,
  spec: ReadyMonthListInvalidation
): void {
  if (spec === 'none') return;
  if (spec === 'all') {
    void queryClient.invalidateQueries(READY_MONTH_INVALIDATE);
    return;
  }
  void queryClient.invalidateQueries({
    queryKey: readyMonthProjectIdsQueryKey(spec.year, spec.month),
    refetchType: 'all',
  });
}

export type ReadyMonthProjectsSyncFlags = {
  invalidateSummaries: boolean;
  readyMonthLists: ReadyMonthListInvalidation;
};

/**
 * После `project:updated` по сокету: не делаем refetch всех раскрытых месяцев,
 * если проект уже в кэше «готовых» и остаётся готовым — достаточно merge.
 */
export function syncReadyMonthCachesAfterProjectUpdated(
  queryClient: QueryClient,
  project: Project
): ReadyMonthProjectsSyncFlags {
  if (project.is_archived) {
    if (projectInAnyReadyMonthCache(queryClient, project.id)) {
      removeProjectFromReadyMonthCachesOnly(queryClient, project.id);
    }
    return { invalidateSummaries: false, readyMonthLists: 'none' };
  }

  const inReadyCache = projectInAnyReadyMonthCache(queryClient, project.id);
  const nowReady = isProjectExcludedFromActiveTable(project.state);
  const bucket = getReadyByMonthBucketFromProject(project);

  if (nowReady && inReadyCache) {
    mergeSingleProjectInGlobalListCaches(queryClient, project);
    if (bucket) {
      removeProjectFromReadyMonthCachesOnly(queryClient, project.id);
      mergeProjectIntoSingleReadyMonthCache(queryClient, bucket, project);
      ensureReadyMonthSummaryRow(queryClient, bucket);
    }
    return { invalidateSummaries: false, readyMonthLists: 'none' };
  }

  if (nowReady && !inReadyCache) {
    if (bucket) {
      ensureReadyMonthSummaryRow(queryClient, bucket);
      if (queryClient.getQueryData<string[]>(readyMonthProjectIdsQueryKey(bucket.year, bucket.month)) !== undefined) {
        mergeProjectIntoSingleReadyMonthCache(queryClient, bucket, project);
      }
      return {
        invalidateSummaries: false,
        readyMonthLists: 'none',
      };
    }
    return {
      invalidateSummaries: true,
      readyMonthLists: 'all',
    };
  }

  if (!nowReady && inReadyCache) {
    removeProjectFromReadyMonthCachesOnly(queryClient, project.id);
    return { invalidateSummaries: false, readyMonthLists: 'none' };
  }

  return { invalidateSummaries: false, readyMonthLists: 'none' };
}

function projectInArchivedCache(queryClient: QueryClient, projectId: string): boolean {
  const ids = queryClient.getQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY);
  return ids?.includes(projectId) ?? false;
}

export type ArchivedProjectsSyncFlags = {
  archivedList: 'none' | 'all';
  invalidateArchivedCount: boolean;
};

/**
 * После project:updated: кэш архива — merge или инвалидация по аналогии с готовыми по месяцам.
 */
export function syncArchivedCachesAfterProjectUpdated(
  queryClient: QueryClient,
  project: Project
): ArchivedProjectsSyncFlags {
  const inArchive = projectInArchivedCache(queryClient, project.id);
  const nowArchived = !!project.is_archived;

  if (nowArchived && inArchive) {
    mergeProjectIntoArchivedCache(queryClient, project);
    return { archivedList: 'none', invalidateArchivedCount: false };
  }

  if (nowArchived && !inArchive) {
    if (queryClient.getQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY) !== undefined) {
      mergeProjectIntoArchivedCache(queryClient, project);
      return { archivedList: 'none', invalidateArchivedCount: true };
    }
    return { archivedList: 'all', invalidateArchivedCount: true };
  }

  if (!nowArchived && inArchive) {
    removeProjectFromArchivedCachesOnly(queryClient, project.id);
    return { archivedList: 'none', invalidateArchivedCount: true };
  }

  return { archivedList: 'none', invalidateArchivedCount: false };
}

export function invalidateArchivedProjectCaches(
  queryClient: QueryClient,
  spec: { list: 'none' | 'all'; count: boolean }
): void {
  if (spec.list === 'all') {
    void queryClient.invalidateQueries({ queryKey: ARCHIVED_PROJECT_IDS_QUERY_KEY, refetchType: 'all' });
  }
  if (spec.count) {
    void queryClient.invalidateQueries({ queryKey: ARCHIVED_COUNT_QUERY_KEY, refetchType: 'all' });
    void queryClient.invalidateQueries({ queryKey: ARCHIVED_PROJECT_IDS_QUERY_KEY, refetchType: 'all' });
  }
}
